import { useState, type FormEvent } from 'react';
import { GlobalButton } from './GlobalButton';
import { GlobalDatepicker } from './GlobalDatepicker';
import { GlobalDropdown } from './GlobalDropdown';
import { GlobalInput } from './GlobalInput';
import { GlobalTextarea } from './GlobalTextarea';
import type { RealEstateProjectPhase, ProjectPhaseStatus } from '../types';

export type ProjectPhaseFormValues = {
  name: string;
  status: ProjectPhaseStatus;
  dependsOn?: string[];
  expectedStartDate?: string;
  expectedEndDate?: string;
  completedDate?: string;
  responsibleParty?: string;
  notes?: string;
};

type Props = {
  phases: RealEstateProjectPhase[];
  phase?: Pick<
    RealEstateProjectPhase,
    | 'name'
    | 'status'
    | 'dependsOn'
    | 'expectedStartDate'
    | 'expectedEndDate'
    | 'completedDate'
    | 'responsibleParty'
    | 'notes'
  >;
  saving?: boolean;
  error?: string | null;
  submitLabel?: string;
  onSubmit: (values: ProjectPhaseFormValues) => void;
  onCancel: () => void;
};

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'blocked', label: 'Blocked' },
];

export function ProjectPhaseForm({
  phases,
  phase,
  saving = false,
  error,
  submitLabel = 'Add phase',
  onSubmit,
  onCancel,
}: Props) {
  const [name, setName] = useState(phase?.name ?? '');
  const [status, setStatus] = useState<ProjectPhaseStatus>(phase?.status ?? 'pending');
  const [dependsOn, setDependsOn] = useState<string[]>(phase?.dependsOn ?? []);
  const [expectedStartDate, setExpectedStartDate] = useState(phase?.expectedStartDate ?? '');
  const [expectedEndDate, setExpectedEndDate] = useState(phase?.expectedEndDate ?? '');
  const [completedDate, setCompletedDate] = useState(phase?.completedDate ?? '');
  const [responsibleParty, setResponsibleParty] = useState(phase?.responsibleParty ?? '');
  const [notes, setNotes] = useState(phase?.notes ?? '');
  const [validationError, setValidationError] = useState<string | null>(null);

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) {
      setValidationError('Phase name is required.');
      return;
    }

    setValidationError(null);
    onSubmit({
      name: trimmedName,
      status,
      dependsOn: dependsOn.length > 0 ? dependsOn : undefined,
      expectedStartDate: expectedStartDate.trim() || undefined,
      expectedEndDate: expectedEndDate.trim() || undefined,
      completedDate: completedDate.trim() || undefined,
      responsibleParty: responsibleParty.trim() || undefined,
      notes: notes.trim() || undefined,
    });
  }

  function toggleDependency(phaseId: string) {
    setDependsOn((prev) =>
      prev.includes(phaseId) ? prev.filter((id) => id !== phaseId) : [...prev, phaseId]
    );
  }

  const displayError = validationError ?? error;

  return (
    <form className="sidebarFormStack" onSubmit={handleSubmit}>
      <GlobalInput
        label="Phase name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="e.g. Architectural Council Approval"
        autoComplete="off"
        required
      />

      <GlobalDropdown
        label="Status"
        value={status}
        onChange={(value) => setStatus(value as ProjectPhaseStatus)}
        options={STATUS_OPTIONS}
      />

      <GlobalDatepicker
        label="Expected start date (optional)"
        value={expectedStartDate}
        onChange={(e) => setExpectedStartDate(e.target.value)}
      />

      <GlobalDatepicker
        label="Expected end date (optional)"
        value={expectedEndDate}
        onChange={(e) => setExpectedEndDate(e.target.value)}
      />

      {status === 'completed' ? (
        <GlobalDatepicker
          label="Completed date"
          value={completedDate}
          onChange={(e) => setCompletedDate(e.target.value)}
        />
      ) : null}

      <GlobalInput
        label="Responsible party (optional)"
        value={responsibleParty}
        onChange={(e) => setResponsibleParty(e.target.value)}
        placeholder="e.g. Architect, Contractor, Municipal office"
        autoComplete="off"
      />

      {phases.length > 0 ? (
        <div>
          <label className="globalInputLabel">Dependencies (optional)</label>
          <p className="muted" style={{ margin: '4px 0 8px', fontSize: '0.8125rem' }}>
            Select phases that must be completed before this one can start.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {phases.map((p) => (
              <label
                key={p.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={dependsOn.includes(p.id)}
                  onChange={() => toggleDependency(p.id)}
                />
                <span>{p.name}</span>
              </label>
            ))}
          </div>
        </div>
      ) : null}

      <GlobalTextarea
        label="Notes (optional)"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Details, contact info, requirements..."
        maxLength={2000}
      />

      {displayError ? <p className="sidebarFormError">{displayError}</p> : null}

      <div className="sidebarFormActions">
        <GlobalButton type="button" variant="secondary" disabled={saving} onClick={onCancel}>
          Cancel
        </GlobalButton>
        <GlobalButton type="submit" variant="primary" isLoading={saving} disabled={saving}>
          {submitLabel}
        </GlobalButton>
      </div>
    </form>
  );
}
